import * as Sentry from "@sentry/vue";
import { normalizeEndpoint, truncate } from '@/utility/sentryFilters';

export default class ErrorHandler {

	/**
	 * Associa (o rimuove) l'utente corrente a tutti gli eventi successivi, compresi quelli
	 * catturati in automatico. Solo l'id: l'email è un dato personale e non serve a Sentry.
	 */
	static setUser(userId?: string | null): void {
		Sentry.setUser(userId ? { id: String(userId) } : null);
	}

	/**
	 * Captures a technical exception to Sentry with HTTP context.
	 * Use in catch blocks for unexpected errors.
	 *
	 * 4xx errors (client/input errors) and connectivity errors are intentionally
	 * skipped — they are expected conditions, not application bugs. 5xx errors are
	 * sent, grouped by method + endpoint + status and without the response body.
	 *
	 * `blocksBoot` marks errors on the critical path of the first render (e.g. the
	 * getUser call the router awaits). Those are never noise: dropped silently they
	 * leave the user in front of a spinner that never goes away and nothing reaches
	 * Sentry, so they bypass the connectivity filter.
	 */
	static handleError(error: any, options: { blocksBoot?: boolean } = {}): void {
		const { blocksBoot = false } = options;
		const status: number | undefined = error?.response?.status;

		// 4xx: client/input errors — not bugs, skip Sentry
		if (status !== undefined && status >= 400 && status < 500) {
			console.warn(
				`[${status}] ${error.config?.method?.toUpperCase() ?? ''} ${error.config?.url ?? ''} —`,
				error.response?.data
			);
			return;
		}

		// Network / connectivity / timeout — transient, skip Sentry
		// unless the failure is what is keeping the app from starting.
		if (this.isConnectivityError(error) && !blocksBoot) {
			console.warn('Network error:', error.message ?? error);
			return;
		}

		Sentry.withScope((scope) => {
			if (blocksBoot) {
				scope.setTag('boot.blocking', 'true');
			}

			const method: string | undefined = error?.config?.method?.toUpperCase();
			const endpoint = normalizeEndpoint(error?.config?.url);

			// Searchable / filterable tags in Sentry dashboard
			if (status !== undefined) {
				scope.setTag('http.status', String(status));
			}
			if (endpoint) {
				scope.setTag('api.endpoint', endpoint);
			}
			if (method) {
				scope.setTag('api.method', method);
			}

			// Structured context visible in the Sentry event detail panel.
			// Il body della risposta non viene inviato: può contenere dati personali.
			scope.setContext('api_request', {
				url: endpoint,
				method: method ?? null,
				status: status ?? null,
				error_code:
					error?.response?.data?.codiceErrore ??
					error?.response?.data?.codError ??
					null,
				error_description: truncate(error?.response?.data?.descrErrore),
			});

			// Stesso endpoint + stesso status = stessa issue, anche se gli id nell'URL cambiano.
			if (endpoint) {
				scope.setFingerprint(['api', method ?? '', endpoint, String(status ?? 'no-status')]);
			}

			scope.setLevel('error');
			Sentry.captureException(error);
		});
	}

	/**
	 * Captures a business/logic event (not a technical exception) to Sentry.
	 * Use when an unexpected business condition occurs that is worth tracking,
	 * even if no exception was thrown (e.g. unexpected API response).
	 *
	 * `data` deve contenere solo valori primitivi già sanificati: mai body di richieste o
	 * risposte, OTP, email o altri dati personali.
	 */
	static captureBusinessError(
		message: string,
		data?: Record<string, string | number | boolean | null>,
		fingerprint: string[] = [message]
	): void {
		Sentry.withScope((scope) => {
			if (data) {
				scope.setContext('details', data);
			}
			scope.setFingerprint(fingerprint);
			Sentry.captureMessage(message, { level: 'warning' });
		});
	}

	/**
	 * Adds a debug breadcrumb to Sentry's event context.
	 * Use to trace the sequence of actions leading up to an error.
	 *
	 * Gli errori e le risposte HTTP vengono ridotti ai soli campi diagnostici: passare un
	 * `AxiosResponse` intero porterebbe con sé header, config e body.
	 */
	static addLog(message: string, data?: any): void {
		Sentry.addBreadcrumb({
			category: 'app.log',
			message,
			level: 'info',
			data: this.toLogData(data),
		});
	}

	private static toLogData(data: any): Record<string, unknown> | undefined {
		if (data === undefined || data === null) {
			return undefined;
		}
		if (data instanceof Error) {
			return { error: data.message };
		}
		// risposta o errore axios
		if (data.config || data.status !== undefined || data.response) {
			const response = data.response ?? data;
			return {
				status: response?.status ?? null,
				url: normalizeEndpoint(data.config?.url ?? response?.config?.url),
				method: (data.config?.method ?? response?.config?.method)?.toUpperCase() ?? null,
			};
		}
		return typeof data === 'object' ? data : { value: String(data) };
	}

	/**
	 * Errori di connettività: rete assente, timeout, richiesta annullata. Non sono bug
	 * dell'app e non dipendono da noi.
	 */
	private static isConnectivityError(error: any): boolean {
		if (['ECONNABORTED', 'ETIMEDOUT', 'ERR_NETWORK', 'ERR_CANCELED'].includes(error?.code)) {
			return true;
		}

		if (error?.name === 'CanceledError' || error?.name === 'AbortError') {
			return true;
		}

		if (
			error?.message &&
			(error.message.includes('Network Error') ||
				error.message.includes('network error') ||
				error.message.includes('Failed to fetch'))
		) {
			return true;
		}

		// Richiesta partita ma nessuna risposta — problema di connettività
		return !!error?.isAxiosError && !error.response;
	}
}
